/**
 * Control Room · Offisiell liste (sak 247, migreringene 0900–0902).
 *
 * Den offisielle artistlista bor i plattformorganisasjonen og publiseres til hver organisasjon med
 * Booking. Bare for plattformadmin; alle andre får 404 (`@OrgScope('none')` + `PlatformAdminGuard`).
 *
 *   GET    /v1/platform/official/artists?q=&agencyId=&sort=&direction=&cursor=&limit=
 *   POST   /v1/platform/official/artists            → OfficialArtist (lagringen publiseres av seg selv)
 *   PATCH  /v1/platform/official/artists/:id        → OfficialArtist
 *   DELETE /v1/platform/official/artists/:id        → 204
 *   GET    /v1/platform/official/agencies           → { items }
 *   POST   /v1/platform/official/agencies           → OfficialAgency
 *   PATCH  /v1/platform/official/agencies/:id       → OfficialAgency
 *   DELETE /v1/platform/official/agencies/:id       → 204 (bare uten artister: VALIDATION_FAILED /
 *                                                     `agency_has_artists` ellers)
 *   GET    /v1/platform/official/review             → { items } («Må sjekkes» fra siste skanning)
 *   POST   /v1/platform/official/review/:id/resolve → 204
 *   POST   /v1/platform/official/publish            → OfficialPublishResult
 *   GET    /v1/platform/official/summary            → OfficialSummary
 *
 * Bare de offisielle feltene. Ingen pris en organisasjon har avtalt eller bekreftet, ingen notater og
 * ingen status finnes i den offisielle lista.
 */
import { z } from 'zod';

import { bookingGenderSchema } from '../enums.js';
import { cursorQuerySchema, page } from '../http/pagination.js';
import { urlInputSchema } from '../url.js';
import { AGENCY_REVIEW_KINDS } from '../booking/agency-scan.js';
import { MAX_BOOKING_LABEL, MAX_BOOKING_NAME, MAX_PARTY_SIZE } from './booking-register.js';

export const OFFICIAL_IDENTITY_STATUSES = [
  'unverified',
  'verified',
  'ambiguous',
  'not_found',
] as const;
export type OfficialIdentityStatus = (typeof OFFICIAL_IDENTITY_STATUSES)[number];

export const officialArtistSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    agencyId: z.uuid().nullable(),
    agencyName: z.string().nullable(),
    genre: z.string().nullable(),
    artistType: z.string().nullable(),
    homeTown: z.string().nullable(),
    gender: bookingGenderSchema.nullable(),
    frontPerson: z.string().nullable(),
    ageGroup: z.string().nullable(),
    spotifyUrl: z.string().nullable(),
    spotifyId: z.string().nullable(),
    identityStatus: z.enum(OFFICIAL_IDENTITY_STATUSES),
    instagramHandle: z.string().nullable(),
    instagramFollowers: z.number().int().nonnegative().nullable(),
    imageUrl: z.string().nullable(),
    monthlyListeners: z.number().int().nonnegative().nullable(),
    /** Antallet som reiser, når noen har satt det. Følger ikke med ut til organisasjonene (0999). */
    partySize: z.number().int().min(1).nullable(),
    /* Det offisielle anslaget (feeMinor, estimateLow/HighMinor) er borte (0999, sak 272). */
    /** Når skanningen fant artisten, eller null. */
    scanDiscoveredAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'OfficialArtist' });
export type OfficialArtist = z.infer<typeof officialArtistSchema>;

export const OFFICIAL_ARTIST_SORT_KEYS = ['name', 'listeners', 'updated'] as const;
export type OfficialArtistSortKey = (typeof OFFICIAL_ARTIST_SORT_KEYS)[number];

export const listOfficialArtistsQuerySchema = cursorQuerySchema(OFFICIAL_ARTIST_SORT_KEYS)
  .extend({
    /** Søk i navnet, byrået, sjangeren og hjemstedet. */
    q: z.string().trim().max(100).optional(),
    agencyId: z.uuid().optional(),
  })
  .meta({ id: 'ListOfficialArtistsQuery' });
export type ListOfficialArtistsQuery = z.infer<typeof listOfficialArtistsQuerySchema>;

export const officialArtistListResponseSchema = page(
  officialArtistSchema,
  'OfficialArtistListResponse',
)
  .extend({
    /** Hvor mange artister filtrene gir (som bookingregisteret, sak 91). */
    total: z.number().int().nonnegative(),
  })
  .meta({ id: 'OfficialArtistListResponse' });
export type OfficialArtistListResponse = z.infer<typeof officialArtistListResponseSchema>;

const label = z.string().trim().min(1).max(MAX_BOOKING_LABEL);

/**
 * De redigerbare offisielle feltene. `name` er påkrevd for en ny artist. Spotify-id, identitet,
 * lyttertall og «funnet av skanningen» settes av skanningen og innhentingen, ikke her.
 */
export const createOfficialArtistRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(MAX_BOOKING_NAME),
    agencyId: z.uuid().nullish(),
    genre: label.nullish(),
    artistType: label.nullish(),
    homeTown: label.nullish(),
    gender: bookingGenderSchema.nullish(),
    frontPerson: z.string().trim().min(1).max(MAX_BOOKING_NAME).nullish(),
    ageGroup: label.nullish(),
    spotifyUrl: urlInputSchema(500).nullish(),
    /** Uten @ og uten URL. */
    instagramHandle: z.string().trim().min(1).max(60).nullish(),
    instagramFollowers: z.number().int().min(0).nullish(),
    imageUrl: urlInputSchema(500).nullish(),
    partySize: z.number().int().min(1).max(MAX_PARTY_SIZE).nullish(),
  })
  .meta({ id: 'CreateOfficialArtistRequest' });
export type CreateOfficialArtistRequest = z.infer<typeof createOfficialArtistRequestSchema>;

/** De samme feltene, alle valgfrie. Et felt som ikke står i kroppen, røres ikke. */
export const updateOfficialArtistRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(MAX_BOOKING_NAME).optional(),
    agencyId: z.uuid().nullish(),
    genre: label.nullish(),
    artistType: label.nullish(),
    homeTown: label.nullish(),
    gender: bookingGenderSchema.nullish(),
    frontPerson: z.string().trim().min(1).max(MAX_BOOKING_NAME).nullish(),
    ageGroup: label.nullish(),
    spotifyUrl: urlInputSchema(500).nullish(),
    instagramHandle: z.string().trim().min(1).max(60).nullish(),
    instagramFollowers: z.number().int().min(0).nullish(),
    imageUrl: urlInputSchema(500).nullish(),
    partySize: z.number().int().min(1).max(MAX_PARTY_SIZE).nullish(),
  })
  .meta({ id: 'UpdateOfficialArtistRequest' });
export type UpdateOfficialArtistRequest = z.infer<typeof updateOfficialArtistRequestSchema>;

export const officialAgencySchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    websiteUrl: z.string().nullable(),
    /** Siden skanningen leser artistene fra (sak 243). */
    artistPageUrl: z.string().nullable(),
    artistCount: z.number().int().nonnegative(),
    /** Når skanningen sist leste byrået (QA13 #3). Null: aldri. */
    lastScannedAt: z.iso.datetime().nullable(),
    /** Organisasjonen i Tvitvi som ER byrået (0940, 253.68). Null: ikke koblet. */
    linkedOrganization: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    /** Skjult for organisasjonene siden da (1336). Null: synlig. */
    hiddenAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'OfficialAgency' });
export type OfficialAgency = z.infer<typeof officialAgencySchema>;

/** Ikke paget: byråene er et par hundre, og velgeren viser dem alle. */
export const officialAgencyListResponseSchema = z
  .object({ items: z.array(officialAgencySchema) })
  .meta({ id: 'OfficialAgencyListResponse' });
export type OfficialAgencyListResponse = z.infer<typeof officialAgencyListResponseSchema>;

export const createOfficialAgencyRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(MAX_BOOKING_NAME),
    websiteUrl: urlInputSchema(2048).nullish(),
    artistPageUrl: urlInputSchema(2048).nullish(),
  })
  .meta({ id: 'CreateOfficialAgencyRequest' });
export type CreateOfficialAgencyRequest = z.infer<typeof createOfficialAgencyRequestSchema>;

export const updateOfficialAgencyRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(MAX_BOOKING_NAME).optional(),
    /** Null tar adressen bort. */
    websiteUrl: urlInputSchema(2048).nullish(),
    artistPageUrl: urlInputSchema(2048).nullish(),
    /** Skjul byrået og artistene som bare står hos det, for organisasjonene (1336); false viser det igjen. */
    hidden: z.boolean().optional(),
  })
  .meta({ id: 'UpdateOfficialAgencyRequest' });
export type UpdateOfficialAgencyRequest = z.infer<typeof updateOfficialAgencyRequestSchema>;

/** Detaljkodene en sletting eller lagring kan svare med (VALIDATION_FAILED). */
export const OFFICIAL_REFUSAL_CODES = [
  'agency_has_artists',
  'unknown_agency',
  'invalid_url',
] as const;
export type OfficialRefusalCode = (typeof OFFICIAL_REFUSAL_CODES)[number];

export const officialReviewItemSchema = z
  .object({
    id: z.uuid(),
    kind: z.enum(AGENCY_REVIEW_KINDS),
    artistName: z.string().nullable(),
    agencyId: z.uuid().nullable(),
    agencyName: z.string().nullable(),
    /** Artistene i den offisielle lista funnet kan gjelde (id-ene er offisielle artister). */
    candidates: z.array(z.object({ id: z.uuid(), name: z.string() })),
    evidenceUrl: z.string().nullable(),
    /** Tallene bak funnet: `previous`/`current` (antall hos byrået), `agencies`, `domain`. */
    detail: z.record(z.string(), z.unknown()),
    createdAt: z.iso.datetime(),
    resolvedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'OfficialReviewItem' });
export type OfficialReviewItem = z.infer<typeof officialReviewItemSchema>;

export const officialReviewListResponseSchema = z
  .object({ items: z.array(officialReviewItemSchema) })
  .meta({ id: 'OfficialReviewListResponse' });
export type OfficialReviewListResponse = z.infer<typeof officialReviewListResponseSchema>;

/** Tallene fra en publisering, summert over organisasjonene. */
export const officialPublishResultSchema = z
  .object({
    organizations: z.number().int().nonnegative(),
    added: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    /** Artister der organisasjonens overstyring i årgangen sto, og funnet ble lagret i stedet. */
    protected: z.number().int().nonnegative(),
    agencies: z.number().int().nonnegative(),
    moves: z.number().int().nonnegative(),
  })
  .meta({ id: 'OfficialPublishResult' });
export type OfficialPublishResult = z.infer<typeof officialPublishResultSchema>;

export const officialSummarySchema = z
  .object({
    artists: z.number().int().nonnegative(),
    agencies: z.number().int().nonnegative(),
    lastScanAt: z.iso.datetime().nullable(),
    lastPublishedAt: z.iso.datetime().nullable(),
    /** Uavklarte funn under «Må sjekkes» fra den siste skanningen. */
    openReview: z.number().int().nonnegative(),
  })
  .meta({ id: 'OfficialSummary' });
export type OfficialSummary = z.infer<typeof officialSummarySchema>;

/*
 * ================================================================================================
 * BYRÅET SOM ER ET HUS (QA13 #5, 253.68; migrering 0940).
 *
 *   GET /v1/platform/organizations/:id/official-agency → ControlRoomOrganizationOfficialAgency
 *   PUT /v1/platform/organizations/:id/official-agency { officialAgencyId | null }
 *
 * Koblingen legger byråets artister fra lista i stallen, merket «fra den offisielle lista». Ved neste
 * publisering peker hver festivals kopi av byrået på huset, og bud går rett dit.
 * ================================================================================================
 */
export const OFFICIAL_AGENCY_LINK_REFUSAL_CODES = [
  'not_an_agency',
  'unknown_agency',
  'taken',
  'platform_organization',
] as const;
export type OfficialAgencyLinkRefusalCode = (typeof OFFICIAL_AGENCY_LINK_REFUSAL_CODES)[number];

export const controlRoomOrganizationOfficialAgencySchema = z
  .object({
    organizationId: z.uuid(),
    /** Null: organisasjonen er ikke koblet til et byrå i lista. */
    agency: z
      .object({
        id: z.uuid(),
        name: z.string(),
        artistCount: z.number().int().nonnegative(),
        linkedAt: z.iso.datetime().nullable(),
        linkedByEmail: z.string().nullable(),
        /** Festivalkopier som alt sender bud hit. */
        routed: z.number().int().nonnegative(),
        /**
         * Festivalkopier uten peker. Siden A12 (268.3 a, 1030) får kopiene pekeren i samme øyeblikk som
         * koblingen, så tallet er 0 etter en lagring; det står for kopier som kom til etterpå.
         */
        waiting: z.number().int().nonnegative(),
      })
      .nullable(),
    /** Artister lagt i stallen av akkurat denne lagringen; 0 når ingenting ble lagret. */
    copied: z.number().int().nonnegative(),
    /**
     * A12 (268.3 a): sendte bud og forespørsler som ikke var levert, og som denne lagringen leverte
     * til byrået. 0 når ingenting ble lagret. Valgfri, så et eldre svar uten feltet fortsatt leses.
     */
    delivered: z.number().int().nonnegative().optional(),
  })
  .meta({ id: 'ControlRoomOrganizationOfficialAgency' });
export type ControlRoomOrganizationOfficialAgency = z.infer<
  typeof controlRoomOrganizationOfficialAgencySchema
>;

export const setControlRoomOrganizationOfficialAgencyRequestSchema = z
  .object({ officialAgencyId: z.uuid().nullable() })
  .meta({ id: 'SetControlRoomOrganizationOfficialAgencyRequest' });
export type SetControlRoomOrganizationOfficialAgencyRequest = z.infer<
  typeof setControlRoomOrganizationOfficialAgencyRequestSchema
>;
